import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { Title } from '@angular/platform-browser';
import { SeoService } from '../../services/seo.service';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-about',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatIconModule, RouterLink],
  templateUrl: './about.component.html',
  styleUrl: './about.component.scss',
})
export class AboutComponent implements OnInit, OnDestroy {
  private readonly pageTitle = 'Über Rast-WC.de – Saubere Raststätten-Toiletten finden';
  private readonly pageDescription =
    'Rast-WC.de ist die Community-Plattform für saubere WCs an Raststätten. Finde schnell Toiletten, lese Bewertungen, filtere nach Ausstattung und hilf mit, die Autobahn hygienischer zu machen.';

  constructor(
    private title: Title,
    private seo: SeoService,
    @Inject(DOCUMENT) private doc: Document,
  ) {}

  ngOnInit(): void {
    this.title.setTitle(this.pageTitle);
    this.seo.updateMetaDescription(this.pageDescription);

    // Optional canonical link
    this.setCanonical(this.doc.location?.origin + '/ueber-rast-wc');

    // Structured data (Organization + WebSite + FAQPage)
    const structuredData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          name: 'Rast-WC.de',
          url: 'https://rast-wc.de/',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://rast-wc.de/search?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        },
        {
          '@type': 'Organization',
          name: 'Rast-WC.de',
          url: 'https://rast-wc.de/',
          logo: 'https://rast-wc.de/assets/favicon.ico',
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'Was ist Rast-WC.de?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Rast-WC.de ist eine Community-Plattform, die dir hilft, schnell saubere und gut ausgestattete Toiletten an Autobahnraststätten zu finden – mit Bewertungen, Fotos und Filtern.',
              },
            },
            {
              '@type': 'Question',
              name: 'Wie kann ich mitmachen?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Du kannst Orte bewerten, neue Raststätten melden, fehlende Infos ergänzen und Probleme melden. Jede Rückmeldung verbessert die Datenqualität.',
              },
            },
            {
              '@type': 'Question',
              name: 'Kostet Rast-WC.de etwas?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Die Nutzung ist kostenlos. Manche WCs vor Ort können gebührenpflichtig sein – diese Info wird, sofern bekannt, angezeigt.',
              },
            },
          ],
        },
      ],
    };

    this.seo.updateStructuredData(structuredData);
  }

  ngOnDestroy(): void {
    // Let the app restore defaults when navigating away
    this.seo.resetMetaDescription();
  }

  private setCanonical(href: string | undefined) {
    if (!href) return;
    let link: HTMLLinkElement | null = this.doc.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', href);
  }
}
